import React, { useState, useEffect } from 'react';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon,
  Box,
  Avatar,
  Typography,
  Button,
  IconButton,
  styled,
  Divider,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  alpha,
  Tooltip
} from '@mui/material';
import {
  Dashboard,
  Receipt,
  AttachMoney,
  People,
  Settings,
  List as ListIcon,
  AccountBox,
  ExitToApp,
  ChevronLeft,
  Close as CloseIcon,
  Logout as LogoutIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  AccountBalance as AccountBalanceIcon,
  History as HistoryIcon,
  MonetizationOn as MonetizationOnIcon,
  Payments as PaymentsIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Menu as MenuIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  ExpandLess,
  ExpandMore,
  ListAlt as ListAltIcon,
  Assessment as AssessmentIcon,
  Analytics as AnalyticsIcon,
  Update as UpdateIcon,
  EventNote as EventNoteIcon,
  PersonSearch as PersonSearchIcon,
  Map as MapIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { StyledButton } from '../styles/SharedStyles';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  show?: boolean;
  allowedRoles?: string[];
  roles?: string[];
}

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 280,
    boxSizing: 'border-box',
    bgcolor: 'white',
    borderRight: `1px solid ${theme.palette.divider}`,
    height: 'calc(100vh - 64px)',
    position: 'fixed',
    top: '64px',
    [theme.breakpoints.up('md')]: {
      transform: 'none',
      visibility: 'visible',
    },
    overflow: 'hidden',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#bdbdbd',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#9e9e9e',
    },
  },
}));

const Sidebar = ({ open, onClose, isMobile }: SidebarProps) => {
  const { userProfile, signOut, currentBranch, availableBranches, setCurrentBranch } = useAuth();
  const [avatar, setAvatar] = useState<string | null>(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Close sidebar when route changes on mobile
  useEffect(() => {
    if (isMobile && open) {
      onClose();
    }
  }, [location.pathname, isMobile]);

  useEffect(() => {
    const fetchAvatar = async () => {
      if (userProfile) {
        const { data, error } = await supabase
          .from('user_avatars')
          .select('avatar_url')
          .eq('profile_id', userProfile.id)
          .maybeSingle();
        if (!error && data) {
          setAvatar(data.avatar_url);
        } else {
          setAvatar(null);
        }
      }
    };
    fetchAvatar();
  }, [userProfile]);

  // Common menu items shown to every role that can use this (agent-only) app.
  const baseMenuItems: MenuItem[] = [
    { text: 'Collection List', icon: <ListAltIcon />, path: '/collection-list' },
    { text: 'Borrower Updates', icon: <UpdateIcon />, path: '/borrower-updates' },
    { text: 'Profile', icon: <AccountBox />, path: '/profile' },
    { text: 'Leave Application', icon: <EventNoteIcon />, path: '/leave-application', allowedRoles: ['agent', 'superagent', 'admin'] },
    { text: 'Credit Investigation', icon: <PersonSearchIcon />, path: '/ci-queue', allowedRoles: ['agent', 'superagent', 'admin'] }
  ];

  // Build the complete menu based on the logged-in user's role.
  let menuItems: MenuItem[] = [];
  if (userProfile) {
    if (userProfile.role === 'agent') {
      menuItems.push(
        {
          text: 'Agent Dashboard',
          icon: <Dashboard />,
          path: '/agent-dashboard',
          allowedRoles: ['agent', 'superagent']
        },
        {
          text: 'My Borrowers',
          icon: <People />,
          path: '/my-borrowers',
          allowedRoles: ['agent', 'superagent']
        },
        {
          text: 'Itinerary Planner',
          icon: <MapIcon />,
          path: '/itinerary-planner',
          allowedRoles: ['agent', 'superagent']
        },
        {
          text: 'Approved Itinerary',
          icon: <CheckCircleIcon />,
          path: '/approved-itinerary',
          allowedRoles: ['agent', 'superagent']
        }
      );
    } else {
      // Superagents (and admins, who are limited to agent/superagent pages here)
      // see both dashboards and My Borrowers.
      menuItems.push(
        {
          text: 'Collection Dashboard',
          icon: <AccountBalanceWalletIcon />,
          path: '/collection-dashboard',
          allowedRoles: ['superagent', 'admin']
        },
        {
          text: 'Agent Dashboard',
          icon: <Dashboard />,
          path: '/agent-dashboard',
          allowedRoles: ['agent', 'superagent', 'admin']
        },
        {
          text: 'My Borrowers',
          icon: <People />,
          path: '/my-borrowers',
          allowedRoles: ['agent', 'superagent', 'admin']
        },
        {
          text: 'Itinerary Planner',
          icon: <MapIcon />,
          path: '/itinerary-planner',
          allowedRoles: ['agent', 'superagent', 'admin']
        },
        {
          text: 'Approved Itinerary',
          icon: <CheckCircleIcon />,
          path: '/approved-itinerary',
          allowedRoles: ['agent', 'superagent', 'admin']
        }
      );
    }

    // Append the common menu items
    menuItems = [...menuItems, ...baseMenuItems];
  } else {
    // Fallback to base items if no profile
    menuItems = baseMenuItems;
  }

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const drawer = (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100%',
      overflow: 'auto'
    }}>
      <Box sx={{ 
        p: { xs: 2, md: 3 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              src={avatar || undefined}
              alt={userProfile?.username || 'User'}
              sx={{
                width: { xs: 48, md: 56 },
                height: { xs: 48, md: 56 },
                mr: 2,
                bgcolor: theme.palette.primary.main,
                border: '2px solid white',
                boxShadow: 1
              }}
            >
              {!avatar && userProfile?.username?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  lineHeight: 1.2
                }}
              >
                {userProfile?.username || 'Loading...'}
              </Typography>
            </Box>
          </Box>
          {!isMobile && (
            <Tooltip title="Hide Sidebar">
              <IconButton onClick={onClose} size="small">
                <ChevronLeft />
              </IconButton>
            </Tooltip>
          )}
          {isMobile && (
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        <Box sx={{ width: '100%', mt: 2 }}>
          <BranchSwitcher />
        </Box>
      </Box>

      <List sx={{ 
        flex: 1, 
        overflowY: 'auto',
        px: { xs: 1, md: 2 },
        mb: 2,
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#bdbdbd',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#9e9e9e',
        },
      }}>
        {menuItems
          .filter((item) => {
            if (item.allowedRoles || item.roles) {
              const allowed: string[] = item.allowedRoles || item.roles || [];
              return userProfile ? allowed.includes(userProfile.role) : false;
            }
            return true;
          })
          .map((item) => (
            <Tooltip 
              key={item.path}
              title={!open ? item.text : ''}
              placement="right"
            >
              <ListItem 
                button 
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    onClose();
                  }
                }}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.15) },
                  backgroundColor: location.pathname === item.path ? alpha(theme.palette.primary.main, 0.15) : 'inherit',
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === item.path ? theme.palette.primary.main : 'text.secondary', minWidth: { xs: 36, md: 40 } }}>
                  {item.icon}
                </ListItemIcon>
                {open && 
                  <ListItemText primary={item.text} primaryTypographyProps={{ sx: { fontSize: { xs: '0.9rem', md: '1rem' } } }}/>
                }
                {open && location.pathname === item.path && (
                  <KeyboardArrowRightIcon sx={{ color: theme.palette.primary.main, opacity: 0.8 }} />
                )}
              </ListItem>
            </Tooltip>
          ))}
        
        <ListItem 
          button 
          onClick={handleLogout}
          sx={{
            borderRadius: 1,
            mb: 1,
            mt: 2,
            backgroundColor: 'inherit',
            "&:hover": { 
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <ListItemIcon sx={{ color: 'text.secondary', minWidth: { xs: 36, md: 40 } }}>
            <LogoutIcon />
          </ListItemIcon>
          {open && 
            <ListItemText 
              primary="Logout" 
              primaryTypographyProps={{ 
                sx: { 
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  fontWeight: 'normal'
                } 
              }}
            />
          }
        </ListItem>
      </List>
    </Box>
  );

  return (
    <StyledDrawer
      variant={isMobile ? "temporary" : "permanent"}
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMobile ? '85%' : 280, // Adjust width on mobile
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition: theme.transitions.create('transform', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
        display: isMobile && !open ? 'none' : 'block', // Hide completely when closed on mobile
      }}
      ModalProps={{
        keepMounted: true,
      }}
    >
      {drawer}
    </StyledDrawer>
  );
};

const BranchSwitcher = () => {
  const { currentBranch, availableBranches, setCurrentBranch } = useAuth();

  return (
    <Box sx={{ p: 2 }}>
      <FormControl fullWidth size="small">
        <InputLabel>Current Branch</InputLabel>
        <Select
          value={currentBranch?.id || ''}
          label="Current Branch"
          onChange={(e) => {
            const branch = availableBranches.find(b => b.id === e.target.value);
            if (branch) {
              setCurrentBranch(branch);
            }
          }}
        >
          {availableBranches.map((branch) => (
            <MenuItem key={branch.id} value={branch.id}>
              {branch.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default Sidebar;
